/**
 * `useModelEdit()` — TanStack Mutation wrapper for `POST /model-edit`.
 *
 * 2026-05-26 — extended with optimistic-patch (`onMutate`) + server-state
 * merge (`onSuccess`) to consolidate the update path across Workbench /
 * Model / Signal views.
 *
 * Pre-fix flow:
 *   POST → server commits durably → response (narrow, just diff) →
 *   onSuccess invalidates both caches → refetch round-trip → UI updates
 *
 * Pre-fix UX problem: a visible flash where VariablesPanel renders
 * "Property location — not set" between the response landing and the
 * refetch completing, because the response carried only the inputProvenance
 * half of the mutation (not dealMetadata).
 *
 * Post-fix flow:
 *   onMutate (sync)        → snapshot + optimistic write into both caches
 *                            (inputProvenance entry with source:'user' +
 *                             inputValues entry — VariablesPanel + Model +
 *                             Signal all flip instantly to the new value)
 *   POST awaits (server)   → durable commit + Google Places resolve + admin
 *                            dealMetadata write — synchronous; spinner shown
 *                            on the input field during this window
 *   onSuccess              → merge server-authoritative state into both caches
 *                            (full ProvenanceRecord including derived sub-attrs,
 *                             dealMetadata, aerialLatLng) — CITY/STATE/COORDS
 *                             subtext appears the moment the response lands
 *   onError                → roll back to the onMutate snapshot
 *   onSettled              → invalidate both caches as a safety net (idempotent
 *                            with the onSuccess patch; handles any rare race)
 *
 * `retry: 0` is preserved — the wire-format contract uses `editId` for replay
 * dedupe; retry>0 would generate a NEW editId per attempt and bypass dedupe.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type ModelEditResponse } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export interface ModelEditMutationVars {
  conversationId: string;
  namedRange: string;
  value: unknown;
  /**
   * Google Places place_id — present ONLY for dataType:'location' inputs.
   * Triggers the shared resolvePropertyLocation hook post-recordEdit (ADR 0027).
   * Absence ⇒ plain string edit; no resolver call; no paid Google request (ADR 0017).
   */
  placeId?: string;
  /**
   * Serialised AutocompleteSessionToken (JSON.stringify). Optional — resolver
   * succeeds without it via cache-first path (ADR 0017).
   */
  sessionToken?: string;
  /**
   * Provenance source — market-defaults-application Phase 4 fix (2026-06-10).
   *
   * Absent (or 'user') ⇒ manual cell-edit path; server stamps source:'user',
   * confidence:1.0. Optimistic record also reflects source:'user'.
   *
   * 'market_data' ⇒ one-click-apply picker path (useApplyValue → here).
   * Server stamps source:'market_data' via recordEdit. Optimistic record
   * reflects source:'market_data' + detail so the picker chip shows MARKET
   * immediately (no User→Market flash on server refetch).
   */
  source?: 'user' | 'market_data';
  /** Data-provider display name (e.g. 'RentCast', 'FRED'). */
  sourceName?: string;
  /** Canonical URL of the data source. */
  sourceUrl?: string;
  /** ISO-8601 date string indicating when the source data was valid. */
  dataAsOf?: string;
  /**
   * Confidence score 0..1. Absent ⇒ server defaults to 0.9 for
   * source:'market_data', 1.0 for source:'user'.
   */
  confidence?: number;
  /**
   * Free-text explanation from the arm's explain textarea.
   * MUST be sent as field name `note` — the api reads `body.note` and threads
   * it internally to provenance.reasoning → model_edits.source_note.
   * Sending `reasoning` instead of `note` → api drops it → source_note stays
   * null → AC fail. (market-default-apply-ux Phase 2 Session 2.6 — R5.)
   */
  note?: string;
}

/**
 * Snapshot of both caches captured in `onMutate`, used for rollback on error.
 * Stored as plain `unknown` to avoid coupling this hook to the internal cache
 * shapes (which differ between `detail` and `views` keys).
 */
interface MutationContext {
  prevDetail: unknown;
  prevViews: unknown;
}

export function useModelEdit() {
  const queryClient = useQueryClient();
  return useMutation<
    ModelEditResponse,
    ApiError,
    ModelEditMutationVars,
    MutationContext
  >({
    mutationFn: ({
      conversationId,
      namedRange,
      value,
      placeId,
      sessionToken,
      source,
      sourceName,
      sourceUrl,
      dataAsOf,
      confidence,
      note,
    }) =>
      api.postModelEdit(conversationId, {
        namedRange,
        value,
        editId: crypto.randomUUID(),
        // ADR 0027 — only include location fields when present; omitting them
        // signals a plain string edit and avoids a spurious Places API call.
        ...(placeId !== undefined ? { placeId } : {}),
        ...(sessionToken !== undefined ? { sessionToken } : {}),
        // market-defaults-application Phase 4 fix — thread provenance source
        // when present; absence keeps the wire shape byte-identical (back-compat).
        ...(source !== undefined ? { source } : {}),
        ...(sourceName !== undefined ? { sourceName } : {}),
        ...(sourceUrl !== undefined ? { sourceUrl } : {}),
        ...(dataAsOf !== undefined ? { dataAsOf } : {}),
        ...(confidence !== undefined ? { confidence } : {}),
        // market-default-apply-ux Phase 2 Session 2.6 (R5) — field name MUST be
        // `note`, NOT `reasoning`. The api reads body.note and threads it
        // internally: provenance.reasoning → model_edits.source_note.
        // Sending `reasoning` → api drops it → source_note stays null → AC fail.
        ...(note !== undefined ? { note } : {}),
      }),
    retry: 0,
    onMutate: async (vars): Promise<MutationContext> => {
      // Cancel any in-flight refetches so they don't overwrite our patch.
      await queryClient.cancelQueries({
        queryKey: queryKeys.conversations.detail(vars.conversationId),
      });
      await queryClient.cancelQueries({
        queryKey: queryKeys.conversations.views(vars.conversationId),
      });

      // Snapshot for rollback.
      const prevDetail = queryClient.getQueryData(
        queryKeys.conversations.detail(vars.conversationId)
      );
      const prevViews = queryClient.getQueryData(
        queryKeys.conversations.views(vars.conversationId)
      );

      // Build a minimal optimistic ProvenanceRecord. The server will replace
      // this with the full record (incl. sourceDetail / sourceUrl / dataAsOf
      // for Location edits) on `onSuccess`. Reflect the passed source so
      // picker-applied market values show the MARKET chip immediately (no
      // User→Market flash on server refetch). Manual edits (no source) stay
      // source:'user' exactly as before.
      const optimisticSource = vars.source ?? 'user';
      const optimisticConfidence =
        vars.confidence ?? (optimisticSource === 'market_data' ? 0.9 : 1.0);
      const optimisticRecord = {
        inputName: vars.namedRange,
        value: vars.value,
        source: optimisticSource,
        confidence: optimisticConfidence,
        timestamp: new Date().toISOString(),
        // Carry provenance detail when present so the MARKET chip's tooltip
        // shows the source name without waiting for the server refetch.
        ...(vars.sourceName !== undefined
          ? { sourceDetail: vars.sourceName }
          : {}),
        ...(vars.sourceUrl !== undefined ? { sourceUrl: vars.sourceUrl } : {}),
        ...(vars.dataAsOf !== undefined ? { dataAsOf: vars.dataAsOf } : {}),
        // market-default-apply-ux Phase 2 Session 2.6 — thread explain note
        // into the optimistic record's `reasoning` field so the MARKET chip
        // tooltip can surface it instantly without waiting for the server refetch.
        // (The request field is `note`; the server maps it to provenance.reasoning.)
        ...(vars.note !== undefined ? { reasoning: vars.note } : {}),
      };

      // Patch the LEGACY detail cache (used by `useConversation` shim →
      // VariablesPanel). The cache shape carries `agentState` with
      // `inputValues` + `inputProvenance` maps.
      queryClient.setQueryData(
        queryKeys.conversations.detail(vars.conversationId),
        (prev: unknown) => {
          if (!prev || typeof prev !== 'object') return prev;
          const detail = prev as {
            data?: {
              agentState?: {
                inputValues?: Record<string, unknown>;
                inputProvenance?: Record<string, unknown>;
              } | null;
            };
          };
          if (!detail.data?.agentState) return prev;
          return {
            ...detail,
            data: {
              ...detail.data,
              agentState: {
                ...detail.data.agentState,
                inputValues: {
                  ...(detail.data.agentState.inputValues ?? {}),
                  [vars.namedRange]: vars.value,
                },
                inputProvenance: {
                  ...(detail.data.agentState.inputProvenance ?? {}),
                  [vars.namedRange]: optimisticRecord,
                },
              },
            },
          };
        }
      );

      // Patch the SSOT views cache (used by Model + Signal + the shim's
      // reconstituted inputValues map). Shape: { inputs: Record<string, ProvenanceRecord>, ... }
      queryClient.setQueryData(
        queryKeys.conversations.views(vars.conversationId),
        (prev: unknown) => {
          if (!prev || typeof prev !== 'object') return prev;
          const views = prev as { inputs?: Record<string, unknown> };
          if (!views.inputs) return prev;
          return {
            ...views,
            inputs: {
              ...views.inputs,
              [vars.namedRange]: optimisticRecord,
            },
          };
        }
      );

      return { prevDetail, prevViews };
    },
    onError: (_err, vars, context) => {
      // Roll back both caches to their pre-mutation snapshots. `context` is
      // the return value of onMutate; null when the cancel/snapshot phase
      // threw before returning (defensive — TanStack normally guarantees it).
      if (context?.prevDetail !== undefined) {
        queryClient.setQueryData(
          queryKeys.conversations.detail(vars.conversationId),
          context.prevDetail
        );
      }
      if (context?.prevViews !== undefined) {
        queryClient.setQueryData(
          queryKeys.conversations.views(vars.conversationId),
          context.prevViews
        );
      }
    },
    onSuccess: (data, vars) => {
      // Merge server-authoritative state. The onMutate patch already wrote
      // an optimistic shape; this overwrites it with the canonical post-
      // edit state including derived sub-attrs + dealMetadata + aerialLatLng.

      // Detail cache: agentState.inputProvenance ← full records; inputValues ← stateDelta.written
      queryClient.setQueryData(
        queryKeys.conversations.detail(vars.conversationId),
        (prev: unknown) => {
          if (!prev || typeof prev !== 'object') return prev;
          const detail = prev as {
            data?: {
              agentState?: {
                inputValues?: Record<string, unknown>;
                inputProvenance?: Record<string, unknown>;
                dealMetadata?: Record<string, unknown> | null;
                aerialLatLng?: { lat: number; lng: number } | null;
              } | null;
            };
          };
          if (!detail.data?.agentState) return prev;
          return {
            ...detail,
            data: {
              ...detail.data,
              agentState: {
                ...detail.data.agentState,
                inputValues: {
                  ...(detail.data.agentState.inputValues ?? {}),
                  ...data.stateDelta.written,
                },
                inputProvenance: {
                  ...(detail.data.agentState.inputProvenance ?? {}),
                  ...data.inputProvenance,
                },
                // Only overwrite dealMetadata + aerialLatLng when the server
                // returned them (Location edits). For scalar edits these are
                // null/undefined on the response and we preserve the existing
                // value via the conditional spread.
                ...(data.dealMetadata !== undefined
                  ? { dealMetadata: data.dealMetadata }
                  : {}),
                ...(data.aerialLatLng !== undefined
                  ? { aerialLatLng: data.aerialLatLng }
                  : {}),
              },
            },
          };
        }
      );

      // Views cache: inputs[k] ← full ProvenanceRecord; dealMetadata + aerialLatLng overwrite
      queryClient.setQueryData(
        queryKeys.conversations.views(vars.conversationId),
        (prev: unknown) => {
          if (!prev || typeof prev !== 'object') return prev;
          const views = prev as {
            inputs?: Record<string, unknown>;
            dealMetadata?: Record<string, unknown> | null;
            aerialLatLng?: { lat: number; lng: number } | null;
          };
          if (!views.inputs) return prev;
          return {
            ...views,
            inputs: {
              ...views.inputs,
              ...data.inputProvenance,
            },
            ...(data.dealMetadata !== undefined
              ? { dealMetadata: data.dealMetadata }
              : {}),
            ...(data.aerialLatLng !== undefined
              ? { aerialLatLng: data.aerialLatLng }
              : {}),
          };
        }
      );
    },
    onSettled: (_data, _err, vars) => {
      // Safety-net invalidation. Idempotent with the onSuccess patch; handles
      // any race where the patch was skipped (e.g. the cache entry didn't
      // exist yet because the page was navigating).
      queryClient.invalidateQueries({
        queryKey: queryKeys.conversations.detail(vars.conversationId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.conversations.views(vars.conversationId),
      });
    },
  });
}
